/* eslint-disable react/destructuring-assignment */
import React, { useEffect, useRef, useState } from 'react'
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager'
import { useCookies } from 'react-cookie'
import {
  MyLocationBanner,
  MyLocationBannerProps,
  MyLocationModal,
  MyLocationModalProps
} from '@dcxwam/dcx-wam-ui-foundations'
import { MyLocationProps } from './types'
import {
  getCurrentUsStateCookie,
  hasUsCommercializationStateChanged,
  isFiveDigits
} from './utils'
import { PageConfig } from '../../../utils/page-config'
import { useMyLocation } from './my-location.hook'
import { emitChangeMyLocationAppliedAndClosed } from '../../../lib/state-based-content/on-change-my-location-close'
import { OPEN_CHANGE_MY_LOCATION_EVENT } from './open-change-my-location-event'
import { resetCartOnLocationChange } from '../../../services/product/reset-cart-on-location-change'
import { GIGYA_COOKIE_UID, HYBRIS_ACCESS_TOKEN } from '../../../utils/constants'

function shouldHideComponent() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fullConfig = PageConfig.getConfig() as any
  const isWidgetEnabled =
    fullConfig?.changeMyLocationConfig?.enableMyLocationWidget === true
  const isGloballyDisabled = fullConfig?.myLocationDisabled === true

  return !isWidgetEnabled || isGloballyDisabled
}

export const ChangeMyLocationInner: React.FC<MyLocationProps> = (props) => {
  const {
    zipcode,
    statecode,
    isCommercialized,
    isLoading,
    applyNavigatorLocation,
    applyLocationFromInput
  } = useMyLocation()
  const [cookies, setCookie] = useCookies([
    GIGYA_COOKIE_UID,
    HYBRIS_ACCESS_TOKEN
  ])

  const isFirstVisit = !zipcode
  const [isModalOpen, setIsModalOpen] = useState(isFirstVisit)
  const [zipInput, setZipInput] = useState<string>('')
  const [isInputZipLocationValid, setIsInputZipLocationValid] = useState(true)
  /** True after a successful apply; cleared when the modal closes (triggers state-based content revalidation). */
  const pendingStateBasedRevalidateRef = useRef(false)

  useEffect(() => {
    const openModalFromExternal = () => {
      setIsModalOpen(true)
    }
    window.addEventListener(
      OPEN_CHANGE_MY_LOCATION_EVENT,
      openModalFromExternal
    )
    return () => {
      window.removeEventListener(
        OPEN_CHANGE_MY_LOCATION_EVENT,
        openModalFromExternal
      )
    }
  }, [])

  const handleModalClose = () => {
    if (pendingStateBasedRevalidateRef.current) {
      emitChangeMyLocationAppliedAndClosed()
      pendingStateBasedRevalidateRef.current = false
      if (!AuthoringUtils.isInEditor() && typeof window !== 'undefined') {
        window.location.reload()
        return
      }
    }
    setZipInput('')
    setIsModalOpen(false)
  }

  const resetCartIfCommercializationStateChanged = async (
    previousState?: string | null
  ) => {
    const nextState = getCurrentUsStateCookie()
    if (!hasUsCommercializationStateChanged(previousState, nextState)) {
      return
    }
    await resetCartOnLocationChange({
      cookies: {
        gig_uid: cookies.gig_uid,
        hy_at: cookies.hy_at
      },
      setCookie: (name, value, options) => {
        setCookie(name as typeof HYBRIS_ACCESS_TOKEN, value, options)
      },
      state: nextState
    })
  }

  const handleUseLocationFromInput = async () => {
    const closeAfterConfirm = isFirstVisit
    if (!isFiveDigits(zipInput) || isLoading) {
      setIsInputZipLocationValid(false)
      return
    }
    const locationChanged = zipcode !== zipInput
    const previousState = getCurrentUsStateCookie() || statecode
    const { success } = await applyLocationFromInput(zipInput)
    setIsInputZipLocationValid(success)
    if (success && locationChanged) {
      pendingStateBasedRevalidateRef.current = true
      await resetCartIfCommercializationStateChanged(previousState)
    }
    if (success && closeAfterConfirm) {
      handleModalClose()
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleInputChange = (event: { target: { value: any } }) => {
    const inputValue = event.target.value
    if (!Number.isNaN(+inputValue) && inputValue.length <= 5) {
      setZipInput(inputValue)
      setIsInputZipLocationValid(inputValue.length >= 5)
    }
  }

  const handleUseCurrentLocation = async () => {
    const previousState = getCurrentUsStateCookie() || statecode
    const { success } = await applyNavigatorLocation()
    if (success) {
      pendingStateBasedRevalidateRef.current = true
      setIsInputZipLocationValid(true)
      await resetCartIfCommercializationStateChanged(previousState)
    } else {
      setIsInputZipLocationValid(false)
    }
  }

  const bannerProps: MyLocationBannerProps = {
    statecode: statecode || '',
    zipcode: zipcode || '',
    onClick: (): void => {
      setIsModalOpen(true)
    },
    myLocationLabel: props.shippingToLabel,
    isCommercialized,
    errorMessage: props.unshippedLocationMessage
  }

  const modalProps: MyLocationModalProps = {
    inputValue: zipInput || '',
    isOpen: isModalOpen,
    handleClose: handleModalClose,
    primaryButton: {
      label: props.applyButtonLabel || '',
      onClick: () => handleUseLocationFromInput()
    },
    secondaryButton: {
      label: props.continueButtonLabel || '',
      onClick: handleModalClose
    },
    useCurrentLocationButton: {
      label: props.useCurrentLocationLabel || '',
      onClick: () => handleUseCurrentLocation()
    },
    firstVisit: isFirstVisit,
    isValidInput: isInputZipLocationValid,
    isValidZipcode: isInputZipLocationValid,
    content: {
      imageSrc: props.firstVisitImage?.assetFileReference || '',
      title: props.dialogTitle || '',
      firstVisitTitle: props.firstVisitTitle || '',
      firstVisitDescription: props.firstVisitDescription || '',
      modalDesription: props.dialogDescription || '',
      invalidZipcodeMessage: props.zipCodeInputError || '',
      zipInputLabel: props.zipCodeLabel || '',
      zipInputPlaceholder: '',
      inputErrorMessage: props.zipCodeInputError || '',
      instructionMessage: props.footerInstructions || '',
      shipmentErrorMessage:
        (isCommercialized
          ? props.availableMessage
          : props.unavailableMessage) || '',
      messageType: isCommercialized ? 'success' : 'error'
    },
    onInputChange: handleInputChange,
    loading: isLoading
  }

  if (shouldHideComponent()) {
    return null
  }

  return (
    <>
      <MyLocationBanner {...bannerProps} />
      <MyLocationModal {...modalProps} />
    </>
  )
}
